<div class="patrol-detail-drawer">
  <!-- Header -->
  <div class="drawer-header">
    <mat-icon class="header-icon">directions_car</mat-icon>
    <h3 class="header-title">Patrol Details</h3>
  </div>

  <!-- Refresh Button -->
  <div class="refresh-section">
    <button
      mat-stroked-button
      class="refresh-button"
      (click)="onRefreshPatrolData()"
      [disabled]="loading$ | async"
    >
      <mat-icon *ngIf="!(loading$ | async)">refresh</mat-icon>
      <mat-spinner
        *ngIf="loading$ | async"
        diameter="16"
        class="button-spinner"
      ></mat-spinner>
      Refresh Data
    </button>
  </div>

  <!-- Content -->
  <div class="drawer-content">
    <!-- No Assignment State -->
    <div *ngIf="!(hasAssignment$ | async)" class="empty-state">
      <mat-icon class="empty-icon">cancel</mat-icon>
      <div class="empty-title">No Patrol Assignment</div>
      <div class="empty-description">
        You don't have any patrol assignments for any geofences.
      </div>
    </div>

    <!-- Assignment State -->
    <ng-container *ngIf="hasAssignment$ | async">
      <!-- Map Section -->
      <div class="section map-section">
        <div class="section-header">
          <mat-icon class="section-icon">public</mat-icon>
          <h4 class="section-title">LIVE LOCATION MAP</h4>
        </div>

        <div class="map-container">
          <div *ngIf="!isGoogleMapsLoaded" class="loading-overlay">
            <mat-spinner diameter="50"></mat-spinner>
            <div class="loading-text">Loading Google Maps...</div>
          </div>

          <google-map
            *ngIf="isGoogleMapsLoaded"
            [center]="mapCenter"
            [zoom]="mapZoom"
            [options]="mapOptions"
            width="100%"
            height="300px"
            (mapInitialized)="onMapReady()"
          >
            <!-- Current Location Marker -->
            <map-marker
              *ngIf="currentLocationMarker"
              [position]="currentLocationMarker.position"
              [options]="
                currentLocationMarker.icon
                  ? { icon: currentLocationMarker.icon }
                  : {}
              "
              [title]="'Your Current Location'"
            >
            </map-marker>

            <!-- Geofence Circle -->
            <map-circle
              *ngIf="
                assignedGeofences.length > 0 &&
                assignedGeofences[0].type === 'circle'
              "
              [center]="{
                lat: assignedGeofences[0].centerLatitude!,
                lng: assignedGeofences[0].centerLongitude!
              }"
              [radius]="assignedGeofences[0].radiusMeters!"
              [options]="geofenceShapeOptions"
            ></map-circle>

            <!-- Geofence Center Marker -->
            <map-marker
              *ngIf="
                assignedGeofences.length > 0 &&
                assignedGeofences[0].type === 'circle'
              "
              [position]="geofenceCenterMarker?.position"
              [options]="{
                icon: geofenceCenterMarker?.icon
              }"
              [title]="geofenceCenterMarker?.label"
            >
            </map-marker>

            <!-- Geofence Polygon -->
            <map-polygon
              *ngIf="
                assignedGeofences.length > 0 &&
                assignedGeofences[0].type === 'polygon' &&
                assignedGeofences[0].polygonVertices
              "
              [paths]="polygonPaths$ | async"
              [options]="geofenceShapeOptions"
            ></map-polygon>
          </google-map>

          <!-- Map Overlay Stats -->
          <div class="map-stats-overlay" *ngIf="isMonitoring">
            <div class="stat-item">
              <mat-icon class="stat-icon success">check_circle</mat-icon>
              <span>In Zone: {{ inZoneChecks }}</span>
            </div>
            <div class="stat-item">
              <mat-icon class="stat-icon danger">cancel</mat-icon>
              <span>Out Zone: {{ outZoneChecks }}</span>
            </div>
            <div class="stat-item">
              <mat-icon class="stat-icon info">schedule</mat-icon>
              <span>Checks: {{ totalChecks }}</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Monitoring Stats Card (Only show when monitoring) -->
      <div class="section monitoring-section" *ngIf="isMonitoring">
        <div class="section-header">
          <mat-icon class="section-icon">dashboard</mat-icon>
          <h4 class="section-title">REAL-TIME MONITORING</h4>
        </div>

        <div class="monitoring-card">
          <div class="monitoring-header">
            <div class="monitoring-status">
              <span
                class="status-indicator"
                [style.background]="monitoringStatusColor$ | async"
              ></span>
              <span class="status-text">{{
                monitoringStatusText$ | async
              }}</span>
            </div>
            <div class="last-check" *ngIf="lastCheckTime">
              <mat-icon class="small-icon">schedule</mat-icon>
              Last: {{ lastCheckTime | date : "shortTime" }}
            </div>
          </div>

          <div class="monitoring-stats">
            <div class="monitoring-stat">
              <div class="stat-value info">{{ totalChecks }}</div>
              <div class="stat-label">Total Checks</div>
            </div>
            <div class="monitoring-stat">
              <div class="stat-value success">{{ inZoneChecks }}</div>
              <div class="stat-label">In Zone</div>
            </div>
            <div class="monitoring-stat">
              <div class="stat-value danger">{{ outZoneChecks }}</div>
              <div class="stat-label">Out Zone</div>
            </div>
            <div class="monitoring-stat">
              <div class="stat-value warning">{{ violationCount }}</div>
              <div class="stat-label">Violations</div>
            </div>
          </div>

          <div class="compliance-meter">
            <div class="meter-label">
              <span>Compliance Rate</span>
              <span class="meter-value">
                {{ (complianceRate$ | async)?.toFixed(1) }}%
              </span>
            </div>
            <mat-progress-bar
              mode="determinate"
              [value]="complianceRate$ | async"
              [color]="
                (complianceRate$ | async) < 70
                  ? 'warn'
                  : (complianceRate$ | async) < 90
                  ? 'accent'
                  : 'primary'
              "
            ></mat-progress-bar>
          </div>
        </div>
      </div>

      <!-- Current Location Card -->
      <div class="section">
        <div class="section-header">
          <mat-icon class="section-icon">location_on</mat-icon>
          <h4 class="section-title">YOUR CURRENT LOCATION</h4>
        </div>

        <div class="info-card location-card">
          <div class="info-row">
            <span class="info-label">Coordinates:</span>
            <span class="info-value monospace">{{
              currentLocationDetails$ | async
            }}</span>
          </div>
          <div class="info-row">
            <span class="info-label">Status:</span>
            <span class="info-value">
              <span
                *ngIf="currentLocation; else noLocation"
                class="status-success"
              >
                <mat-icon class="inline-icon">check_circle</mat-icon>
                Location Detected
              </span>
              <ng-template #noLocation>
                <span class="status-error">
                  <mat-icon class="inline-icon">error</mat-icon>
                  Not Available
                </span>
              </ng-template>
            </span>
          </div>
          <div class="info-row" *ngIf="assignedGeofences.length > 0">
            <span class="info-label">Geofence:</span>
            <span class="info-value">
              <span
                class="geofence-badge"
                [style.background]="assignedGeofences[0].color || '#E93578'"
              >
                {{ assignedGeofences[0].name }}
              </span>
            </span>
          </div>
        </div>
      </div>

      <!-- Patrol Actions Card -->
      <div class="section">
        <div class="section-header">
          <mat-icon class="section-icon">rocket_launch</mat-icon>
          <h4 class="section-title">QUICK ACTIONS</h4>
        </div>

        <div class="actions-container">
          <!-- Start Patrol Button -->
          <button
            mat-raised-button
            class="action-button start-button"
            (click)="onPatrolAction(patrolActions[0])"
            [disabled]="patrolActions[0].disabled || startingPatrol"
          >
            <mat-icon class="action-icon">play_circle</mat-icon>
            <span class="action-label">Start Patrol</span>
          </button>

          <!-- Stop Patrol Button -->
          <button
            mat-raised-button
            class="action-button stop-button"
            (click)="onPatrolAction(patrolActions[1])"
            [disabled]="patrolActions[1].disabled || stoppingPatrol"
          >
            <mat-icon class="action-icon">stop_circle</mat-icon>
            <span class="action-label">Stop Patrol</span>
          </button>

          <!-- View Patrols Button -->
          <button
            mat-raised-button
            class="action-button view-button"
            (click)="onPatrolAction(patrolActions[2])"
            [disabled]="patrolActions[2].disabled"
          >
            <mat-icon class="action-icon">history</mat-icon>
            <span class="action-label">Your Patrols</span>
          </button>
        </div>
      </div>

      <!-- Active Session Card -->
      <div class="section" *ngIf="activeSession">
        <div class="section-header">
          <mat-icon class="section-icon">radar</mat-icon>
          <h4 class="section-title">ACTIVE SESSION</h4>
        </div>

        <div class="info-card active-session-card">
          <div class="session-badge">
            <span class="badge-dot"></span>
            <span>Active</span>
          </div>

          <div class="info-row">
            <span class="info-label">Geofence:</span>
            <span class="info-value">{{ activeSession.geofenceName }}</span>
          </div>
          <div class="info-row">
            <span class="info-label">Started At:</span>
            <span class="info-value">{{
              activeSession.startedAt + "Z" | date : "short"
            }}</span>
          </div>
          <div class="info-row">
            <span class="info-label">Duration:</span>
            <span class="info-value">{{ sessionDuration }}</span>
          </div>
          <div class="info-row">
            <span class="info-label">Total Checks:</span>
            <span class="info-value">{{ activeSession.totalChecks || 0 }}</span>
          </div>
          <div class="info-row">
            <span class="info-label">Start Location:</span>
            <span
              class="info-value monospace"
              *ngIf="activeSession.startLatitude"
            >
              {{ activeSession.startLatitude | number : "1.6-6" }},
              {{ activeSession.startLongitude | number : "1.6-6" }}
            </span>
            <span class="info-value" *ngIf="!activeSession.startLatitude">
              N/A
            </span>
          </div>
        </div>
      </div>

      <!-- No Active Session -->
      <div class="section" *ngIf="!activeSession">
        <div class="section-header">
          <mat-icon class="section-icon">radar</mat-icon>
          <h4 class="section-title">ACTIVE SESSION</h4>
        </div>

        <div class="empty-session">
          <mat-icon class="empty-session-icon">pause_circle</mat-icon>
          <span>No Active Session</span>
        </div>
      </div>
    </ng-container>
  </div>

  <!-- Footer -->
  <div class="drawer-footer">
    <button mat-button class="close-button" (click)="closeDrawer()">
      Close
    </button>
  </div>
</div>
